import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { AppValidators } from 'src/app/common/appValidators';
import { Platform, ResourceType } from 'src/app/models/api';
import { SendEmailActionDefinitionModel } from 'src/app/models/api/playbooks/actions/sendEmailProperties';
import { PlaybooksActionDefinitionInput } from 'src/app/models/componentInputs/playbookActionInput.interface';
import { emailTemplatesByTriggerType } from './emailTemplates';
import { PlaybookTrigger } from 'src/app/models/api/playbooks/playbook.model';
import { PlaybooksTriggerActionsService } from 'src/app/services/actions/playbooks/playbooks-trigger-actions/playbooks-trigger-actions.service';
import { TriggerTypes } from 'src/app/models/api/playbooks/triggers/triggerTypes.enum';
import { LabelsProviderPipe } from 'src/app/pipes/labels-provider/labels-provider.pipe';
import { EmailPlaceholder } from 'src/app/services/actions/common/common-actions.service';
import { ViolationsActionsService } from 'src/app/services/actions/violations/violations-actions.service';
import { InventoryActionsService } from 'src/app/services/actions/inventory/inventory-actions.service';
import { isCreatedBySupported } from './emailTemplates';

@Component({
  selector: 'app-send-email-action',
  templateUrl: './send-email-action.component.html',
  styleUrls: ['./send-email-action.component.scss'],
})
export class SendEmailActionComponent implements OnInit {
  @Input() public fg: UntypedFormGroup;
  @Input() public playbooksActionInput: PlaybooksActionDefinitionInput;
  @Input() public trigger: PlaybookTrigger;

  public withContactMaker = false;
  public contactMakerChecked$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public warningNoRecipientsTooltip = `Zenity will first contact the resource owner. If no owner is identified, the creator of the resource will be contacted, if both are missing the email won't be sent.`;
  public supportedEmailPlaceholders: EmailPlaceholder[] = [];

  private _newSendEmailActionDefinitionModel?: SendEmailActionDefinitionModel;
  private _resourceTypes: Set<ResourceType>;
  private readonly _maxAmountOfEmails: number = 5;

  constructor(
    private readonly _playbooksTriggerActionsService: PlaybooksTriggerActionsService,
    private readonly _labelsProviderPipe: LabelsProviderPipe,
    private readonly _violationsActionsService: ViolationsActionsService,
    private readonly _inventoryActionsService: InventoryActionsService
  ) {}

  ngOnInit(): void {
    let recipientsTextValue = '';

    if (this.playbooksActionInput.actionDefinitionProperties) {
      this._newSendEmailActionDefinitionModel = this.playbooksActionInput
        .actionDefinitionProperties as SendEmailActionDefinitionModel;
      recipientsTextValue = this._newSendEmailActionDefinitionModel.recipients.join('; ');
    }

    const contact_maker_suppoerted_platforms = [Platform.powerplatform, Platform.powerBI];
    this.withContactMaker = contact_maker_suppoerted_platforms.includes(this.playbooksActionInput.platform);

    if (this.withContactMaker) {
      this.fg.addControl(
        'contactMaker',
        new UntypedFormControl(this._newSendEmailActionDefinitionModel?.contactMaker || false, [Validators.required])
      );
      this.contactMakerChecked$.next(this.fg.controls['contactMaker'].value);
      this.fg.addControl(
        'recipients',
        new UntypedFormControl(
          this._newSendEmailActionDefinitionModel?.recipients || [],
          !this.fg.controls['contactMaker'].value ? [Validators.required] : []
        )
      );
      this.fg.addControl(
        'recipientsString',
        new UntypedFormControl(recipientsTextValue, [
          Validators.required,
          AppValidators.multipleEmailsBySeparators(this._maxAmountOfEmails),
        ])
      );

      this.fg.controls['contactMaker'].valueChanges.subscribe((value) => {
        this.contactMakerChecked$.next(value);
        if (value) {
          this.fg.get('recipients')?.setValidators([]);
          this.fg
            .get('recipientsString')
            ?.setValidators([AppValidators.multipleEmailsBySeparators(this._maxAmountOfEmails)]);
        } else {
          this.fg.get('recipients')?.setValidators([Validators.required]);
          this.fg
            .get('recipientsString')
            ?.setValidators([Validators.required, AppValidators.multipleEmailsBySeparators(this._maxAmountOfEmails)]);
        }
        this.fg.get('recipients')?.updateValueAndValidity();
        this.fg.get('recipientsString')?.updateValueAndValidity();
      });
    } else {
      this.fg.addControl(
        'recipients',
        new UntypedFormControl(this._newSendEmailActionDefinitionModel?.recipients || [], [Validators.required])
      );
      this.fg.addControl(
        'recipientsString',
        new UntypedFormControl(recipientsTextValue, [
          Validators.required,
          AppValidators.multipleEmailsBySeparators(this._maxAmountOfEmails),
        ])
      );
    }

    this.fg.addControl(
      'subject',
      new UntypedFormControl(
        this._newSendEmailActionDefinitionModel?.subject ||
          emailTemplatesByTriggerType[this.trigger.triggerType].subject,
        [Validators.required]
      )
    );
    const resource_types = this._playbooksTriggerActionsService.getTriggerResourceTypes(this.trigger);
    this.fg.addControl(
      'bodyText',
      new UntypedFormControl(
        this._newSendEmailActionDefinitionModel?.bodyText ||
          emailTemplatesByTriggerType[this.trigger.triggerType].body(
            this.playbooksActionInput.platform,
            resource_types
          ),
        [Validators.required]
      )
    );

    this.fg.controls['recipientsString'].valueChanges.subscribe((newValue) => {
      if (this.fg.controls['recipientsString'].valid) {
        this.fg.controls['recipients'].setValue(newValue.split(/[,;]+/).map((email: string) => email.trim()));
      }
    });

    this._resourceTypes = this._playbooksTriggerActionsService.getTriggerResourceTypes(this.trigger);
    this.supportedEmailPlaceholders = this._getSupportedEmailPlaceholders();
  }

  private _getSupportedEmailPlaceholders() {
    let placeholders: EmailPlaceholder[] = [];
    switch (this.trigger.triggerType) {
      case TriggerTypes.newViolationFound:
        placeholders = this._violationsActionsService.getViolationEmailPlaceholders();
        break;
      case TriggerTypes.newResourceFound:
        placeholders = this._inventoryActionsService.getEmailPlaceholders(
          isCreatedBySupported(this.playbooksActionInput.platform, this._resourceTypes)
        );
    }
    return placeholders;
  }
}
